import { Button, CurrencyInput, FormGroup } from '@starsoft/common/components';
import { useFormContext } from 'react-hook-form';
import styles from './styles.module.scss';
import { AmountInputProps } from './props';
import { useTranslation, useWithdrawForm } from '@starsoft/common/hooks';
import { WithdrawFormCreateDto } from '../../../../../../dto/withdrawals/baseWithdrawalInputDto/withdraw-create-form.dto';
import { getMoneyOrAmount } from '@starsoft/common/utils';
import { WalletService } from '@starsoft/common/services';

/**
 * AmountInput Component
 * This component is responsible for rendering an input field for entering withdrawal amounts.
 * It includes functionality to handle changes in the input value and to withdraw the full balance.
 *
 * @param {AmountInputProps} props - The properties for the component.
 * @param {boolean} props.showBalance - A flag to determine if the available balance should be displayed.
 *
 * @returns {JSX.Element} The rendered component for inputting withdrawal amounts.
 *
 * @example
 * // Usage example:
 * <AmountInput showBalance={true} />
 */
export default function AmountInput({ showBalance = true }: AmountInputProps) {
  // Retrieve the translation functions for the 'withdraw' and 'notify' namespaces
  const { t } = useTranslation('withdraw');
  const { t: tNotify } = useTranslation('notify');
  // Retrieve form context methods for setting and watching form values
  const { setValue, watch } = useFormContext<WithdrawFormCreateDto>();

  // Retrieve withdrawal form state and methods
  const {
    isOverValued,
    isFormLoading,
    isUnderValued,
    minValueFormatted,
    maxValueFormatted,
    isValidReceiveAmount,
    accountByCoinQuery: { currentAccount, isLoadingAccountByCoin },
    selectedWithdrawalMethodQuery: { coinMapping, isLoadingWithdrawMethod },
    feeQuery: { isLoadingFee },
  } = useWithdrawForm(watch, setValue);

  // Determine if the form is in a loading state
  const isLoading: boolean =
    isLoadingAccountByCoin || isLoadingWithdrawMethod || isFormLoading;
  // Watch the 'amount' field in the form
  const amount = watch('amount');

  /**
   * handleChange Function
   * Updates the 'amount' field in the form with the provided value.
   *
   * @param {number} value - The new value for the 'amount' field.
   * @returns {void}
   *
   * @example
   * handleChange(100);
   */
  function handleChange(value: number) {
    setValue('amount', value, {
      shouldDirty: true,
    });
  }

  /**
   * handleWithdrawAll Function
   * Sets the 'amount' field in the form to the full balance available in the current account.
   *
   * @returns {void}
   *
   * @example
   * handleWithdrawAll();
   */
  function handleWithdrawAll() {
    setValue('amount', getMoneyOrAmount(currentAccount?.balance) ?? 0, {
      shouldDirty: true,
      shouldTouch: true,
    });
  }

  // Render the FormGroup component with a CurrencyInput for entering withdrawal amounts
  return (
    <FormGroup
      label={t('withdraw_title')}
      primary
      className={styles.wrapper}
      isLoading={isFormLoading}
      loading={{ label: true, secondary: false }}
    >
      <CurrencyInput
        coin={currentAccount?.coin}
        value={amount as string}
        hideCurrency
        setValue={handleChange}
        placeholder={t('withdraw_title')}
        hideLock
        isLoading={isLoading}
        isContainerColor
        registerOptions={{
          required: true,
          min: getMoneyOrAmount(coinMapping?.minimum),
          max: coinMapping?.maximum
            ? getMoneyOrAmount(coinMapping?.maximum)
            : undefined,
        }}
        isError={
          (!isValidReceiveAmount || isOverValued || isUnderValued) &&
          !isLoadingFee
        }
        isExternalTouched={!!amount && amount !== 0}
        errorMessage={
          isOverValued || isUnderValued
            ? tNotify('withdraw_amount_error', {
                minAmount: minValueFormatted,
                maxAmount: maxValueFormatted,
              })
            : (t('receive_amount_label', {
                minAmount: minValueFormatted,
                maxAmount: maxValueFormatted,
              }) as string)
        }
        showSpinner={isLoadingFee}
        customIcon={
          !isLoadingFee &&
          !isLoading && (
            <Button
              isLinkButton
              onClick={handleWithdrawAll}
              type="button"
              className={styles.wrapper__button}
            >
              {t('max_label')}
            </Button>
          )
        }
      />
      {showBalance && (
        <div className={styles.wrapper__row}>
          <div className={styles.wrapper__row__label}>
            {t('available_balance_label')}
          </div>
          <div className={styles.wrapper__row__value}>
            {WalletService.maskCurrency({
              amount: getMoneyOrAmount(currentAccount?.balance),
              coin: currentAccount?.coin,
            })}
          </div>
        </div>
      )}
    </FormGroup>
  );
}
